import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    NumberInput,
    Notice,
    SegmentedControl,
    StatusDot,
    Switch,
    TextInput,
} from '@origo/origo-ui';
import { ClockIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ApiError } from '../../api/client';
import { useDeleteSchedule, useSchedules, useUpsertSchedule } from '../../api/hooks';
import {
    DIRECTION_LABEL,
    MIN_INTERVAL_SECONDS,
    policyAllows,
    ScheduleKind,
    SCHEDULE_KIND_LABEL,
    SyncDirection,
    type SyncDirectionPolicy,
    type ScheduleUpsertRequest,
    type SyncSchedule,
} from '../../api/types';
import { PanelError, PanelLoading } from '../common/PanelStates';
import styles from '../common/panel.module.css';

const DIRECTIONS: SyncDirection[] = [SyncDirection.AgolToCentral, SyncDirection.CentralToAgol];
const FORM_ID = 'schedule-form';

export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return '–';
    try {
        return new Date(iso).toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return iso;
    }
}

/** Kort, lesbar beskrivelse av hvor ofte tidsplanen kjører. */
export function scheduleCadence(s: SyncSchedule): string {
    if (s.kind === ScheduleKind.Cron) {
        return s.cronExpression
            ? `${s.cronExpression}${s.timeZoneId ? ` (${s.timeZoneId})` : ''}`
            : 'Cron';
    }
    const sec = s.intervalSeconds ?? 0;
    if (sec % 3600 === 0) {
        const h = sec / 3600;
        return h === 1 ? 'Hver time' : `Hver ${h}. time`;
    }
    if (sec % 60 === 0) {
        const m = sec / 60;
        return m === 1 ? 'Hvert minutt' : `Hvert ${m}. minutt`;
    }
    return `Hvert ${sec}. sekund`;
}

interface ScheduleEditorProps {
    bindingId: string;
    /** Koblingens retningssperre. Utelatt → begge retninger. */
    policy?: SyncDirectionPolicy;
}

/** Tidsstyrt synk: én tidsplan per retning for én kobling, med status (siste/neste kjøring). */
export const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ bindingId, policy }) => {
    const { data: schedules, isLoading, isError, error, refetch } = useSchedules(bindingId);
    const [editing, setEditing] = useState<SyncDirection | null>(null);

    if (isLoading) return <PanelLoading label="Laster tidsplaner…" />;
    if (isError) return <PanelError error={error} onRetry={() => refetch()} />;

    const byDirection = new Map<SyncDirection, SyncSchedule>(
        (schedules ?? []).map((s) => [s.direction, s]),
    );

    return (
        <div className={styles.stack}>
            {DIRECTIONS
                // Sperret retning vises bare hvis det henger igjen en tidsplan der (så den kan slettes).
                .filter((direction) => policyAllows(policy, direction) || byDirection.has(direction))
                .map((direction) => (
                    <DirectionRow
                        key={direction}
                        direction={direction}
                        schedule={byDirection.get(direction) ?? null}
                        onEdit={() => setEditing(direction)}
                        bindingId={bindingId}
                        blocked={!policyAllows(policy, direction)}
                    />
                ))}

            {editing != null && (
                <ScheduleFormDialog
                    bindingId={bindingId}
                    direction={editing}
                    existing={byDirection.get(editing) ?? null}
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
};

interface DirectionRowProps {
    bindingId: string;
    direction: SyncDirection;
    schedule: SyncSchedule | null;
    onEdit: () => void;
    /** Retningen er sperret på koblingen: tidsplanen kjøres ikke og kan bare slettes. */
    blocked: boolean;
}

const DirectionRow: React.FC<DirectionRowProps> = ({ bindingId, direction, schedule, onEdit, blocked }) => {
    const deleteMutation = useDeleteSchedule();
    const [confirmDelete, setConfirmDelete] = useState(false);

    const doDelete = async () => {
        try {
            await deleteMutation.mutateAsync({ bindingId, direction });
            setConfirmDelete(false);
        } catch {
            /* feil vises under */
        }
    };

    const deleteError = deleteMutation.error instanceof ApiError ? deleteMutation.error.message : null;

    const statusText = !schedule ? 'Ingen tidsplan' : schedule.enabled ? 'Aktiv' : 'Pauset';

    return (
        <div className={styles.scheduleRow}>
            <div className={styles.scheduleRowHead}>
                <span className={styles.scheduleRowDir}>
                    {schedule && (
                        <StatusDot variant={schedule.enabled ? 'success' : 'warning'} label={statusText} />
                    )}
                    {DIRECTION_LABEL[direction]}
                </span>
                <span className={styles.hint}>{statusText}</span>
            </div>

            {blocked && (
                <Notice variant="warning">
                    Koblingen er låst til motsatt retning. Denne tidsplanen kjøres ikke — slett den.
                </Notice>
            )}

            {schedule ? (
                <>
                    <div className={styles.metaGrid}>
                        <span className={styles.metaLabel}>Type</span>
                        <span className={styles.metaValue}>{SCHEDULE_KIND_LABEL[schedule.kind]}</span>
                        <span className={styles.metaLabel}>Frekvens</span>
                        <span className={styles.metaValue}>{scheduleCadence(schedule)}</span>
                        <span className={styles.metaLabel}>Siste kjøring</span>
                        <span className={styles.metaValue}>{formatDateTime(schedule.lastRunUtc)}</span>
                        <span className={styles.metaLabel}>Neste kjøring</span>
                        <span className={styles.metaValue}>
                            {schedule.enabled ? formatDateTime(schedule.nextDueUtc) : 'Pauset'}
                        </span>
                    </div>
                    <div className={styles.rowActions}>
                        {!blocked && (
                            <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<PencilSimpleIcon size={16} weight="bold" />}
                                onClick={onEdit}
                            >
                                Rediger
                            </Button>
                        )}
                        <Button
                            variant="danger"
                            size="sm"
                            leftIcon={<TrashIcon size={16} weight="bold" />}
                            onClick={() => setConfirmDelete(true)}
                        >
                            Slett
                        </Button>
                    </div>
                </>
            ) : (
                <div className={styles.rowActions}>
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<PlusIcon size={16} weight="bold" />}
                        onClick={onEdit}
                    >
                        Sett opp tidsplan
                    </Button>
                </div>
            )}

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                title="Slette tidsplan?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={doDelete} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending ? 'Sletter…' : 'Slett tidsplan'}
                        </Button>
                    </>
                }
            >
                <p>
                    Slette tidsplanen for <strong>{DIRECTION_LABEL[direction]}</strong>? Tidsstyrt synk
                    stopper for denne retningen. Manuell og webhook-utløst synk påvirkes ikke.
                </p>
                {deleteError && <Notice variant="danger">{deleteError}</Notice>}
            </Dialog>
        </div>
    );
};

interface ScheduleFormDialogProps {
    bindingId: string;
    direction: SyncDirection;
    existing: SyncSchedule | null;
    onClose: () => void;
}

const ScheduleFormDialog: React.FC<ScheduleFormDialogProps> = ({
    bindingId,
    direction,
    existing,
    onClose,
}) => {
    const upsertMutation = useUpsertSchedule();

    const [enabled, setEnabled] = useState(true);
    const [kind, setKind] = useState<ScheduleKind>(ScheduleKind.Interval);
    const [intervalSeconds, setIntervalSeconds] = useState<number>(300);
    const [cronExpression, setCronExpression] = useState('');
    const [timeZoneId, setTimeZoneId] = useState('Europe/Oslo');
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        setEnabled(existing?.enabled ?? true);
        setKind(existing?.kind ?? ScheduleKind.Interval);
        setIntervalSeconds(existing?.intervalSeconds ?? 300);
        setCronExpression(existing?.cronExpression ?? '');
        setTimeZoneId(existing?.timeZoneId ?? 'Europe/Oslo');
        setValidationError(null);
    }, [existing]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);

        let body: ScheduleUpsertRequest;
        if (kind === ScheduleKind.Interval) {
            if (!Number.isFinite(intervalSeconds) || intervalSeconds < MIN_INTERVAL_SECONDS) {
                setValidationError(`Intervallet må være minst ${MIN_INTERVAL_SECONDS} sekunder.`);
                return;
            }
            body = { kind, enabled, intervalSeconds: Math.round(intervalSeconds) };
        } else {
            if (!cronExpression.trim()) {
                setValidationError('Cron-uttrykk er påkrevd.');
                return;
            }
            body = {
                kind,
                enabled,
                cronExpression: cronExpression.trim(),
                timeZoneId: timeZoneId.trim() || null,
            };
        }

        try {
            await upsertMutation.mutateAsync({ bindingId, direction, body });
            onClose();
        } catch {
            /* server-feil vises via mutation.error under */
        }
    };

    const serverError = upsertMutation.error instanceof ApiError
        ? upsertMutation.error.message
        : upsertMutation.error
            ? 'Lagring feilet. Sjekk tilkobling og token.'
            : null;

    return (
        <Dialog
            open
            onClose={onClose}
            title={`${existing ? 'Rediger' : 'Sett opp'} tidsplan: ${DIRECTION_LABEL[direction]}`}
            footer={
                <>
                    <Button variant="secondary" type="button" onClick={onClose}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" form={FORM_ID} disabled={upsertMutation.isPending}>
                        {upsertMutation.isPending ? 'Lagrer…' : 'Lagre tidsplan'}
                    </Button>
                </>
            }
        >
            <form id={FORM_ID} onSubmit={onSubmit} className={styles.formStack}>
                {(serverError || validationError) && (
                    <Notice variant="danger">{validationError ?? serverError}</Notice>
                )}

                <Switch label="Aktiv" checked={enabled} onChange={setEnabled} />

                <SegmentedControl
                    label="Type tidsplan"
                    value={String(kind)}
                    onChange={(v) => setKind(Number(v) as ScheduleKind)}
                    options={[
                        { value: String(ScheduleKind.Interval), label: SCHEDULE_KIND_LABEL[ScheduleKind.Interval] },
                        { value: String(ScheduleKind.Cron), label: SCHEDULE_KIND_LABEL[ScheduleKind.Cron] },
                    ]}
                />

                {kind === ScheduleKind.Interval ? (
                    <NumberInput
                        label="Intervall (sekunder)"
                        min={MIN_INTERVAL_SECONDS}
                        step={1}
                        value={intervalSeconds}
                        onChange={(e) => setIntervalSeconds(e.target.valueAsNumber)}
                        helperText={`Minst ${MIN_INTERVAL_SECONDS} sekunder mellom kjøringer. F.eks. 300 = hvert 5. minutt.`}
                    />
                ) : (
                    <>
                        <TextInput
                            label="Cron-uttrykk"
                            placeholder="0 * * * *"
                            value={cronExpression}
                            onChange={(e) => setCronExpression(e.target.value)}
                            helperText="Standard cron (min time dag måned ukedag). F.eks. «0 * * * *» = hver hele time."
                        />
                        <TextInput
                            label="Tidssone"
                            placeholder="Europe/Oslo"
                            value={timeZoneId}
                            onChange={(e) => setTimeZoneId(e.target.value)}
                            helperText="IANA-tidssone. Tom = UTC."
                        />
                    </>
                )}

                <p className={styles.hint}>
                    <ClockIcon size={14} weight="bold" />
                    Tidsstyrte kjøringer legges i synk-køen og plukkes opp av worker-en.
                </p>
            </form>
        </Dialog>
    );
};
